import { FC, useMemo } from 'react';
import { format } from 'date-fns';
import { Clock } from 'lucide-react';
import { TimeSlot } from './TimeSlot';
import { Spinner } from '../ui/Spinner';
import { TimeSlotPickerProps, CalendarTimeSlot, TimeSlotGroupProps } from './types';

/**
 * Parse hour from time string (HH:MM)
 */
function getHour(time: string): number {
  const parts = time.split(':');
  const hours = parts[0] || '0';
  return parseInt(hours, 10);
}

/**
 * Group slots by time of day
 */
function groupSlotsByTimeOfDay(slots: CalendarTimeSlot[]): {
  morning: CalendarTimeSlot[];
  afternoon: CalendarTimeSlot[];
  evening: CalendarTimeSlot[];
} {
  const morning: CalendarTimeSlot[] = [];
  const afternoon: CalendarTimeSlot[] = [];
  const evening: CalendarTimeSlot[] = [];

  slots.forEach((slot) => {
    const hour = getHour(slot.startTime);
    if (hour < 12) {
      morning.push(slot);
    } else if (hour < 17) {
      afternoon.push(slot);
    } else {
      evening.push(slot);
    }
  });

  return { morning, afternoon, evening };
}

const TimeSlotGroup: FC<TimeSlotGroupProps> = ({ label, slots, selectedTime, onSelect }) => {
  if (slots.length === 0) return null;

  return (
    <div className="mb-4 last:mb-0">
      <h4 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">{label}</h4>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
        {slots.map((slot) => {
          const isSelected = selectedTime === slot.startTime;

          return (
            <TimeSlot
              key={slot.startTime}
              slot={slot}
              isSelected={isSelected}
              onClick={() => onSelect(slot)}
            />
          );
        })}
      </div>
    </div>
  );
};

export const TimeSlotPicker: FC<TimeSlotPickerProps> = ({
  slots,
  selectedTime,
  onTimeSelect,
  isLoading = false,
  selectedDate,
}) => {
  // Filter to only available slots and group by time of day
  const { morning, afternoon, evening } = useMemo(() => {
    const availableSlots = slots.filter((s) => s.isAvailable);
    return groupSlotsByTimeOfDay(availableSlots);
  }, [slots]);

  const unavailableCount = slots.filter((s) => !s.isAvailable).length;
  const hasAvailableSlots = morning.length > 0 || afternoon.length > 0 || evening.length > 0;

  const formattedDate = format(selectedDate, 'EEEE, MMMM d');

  return (
    <div className="mt-6">
      <div className="flex items-center gap-2 mb-4">
        <Clock className="h-5 w-5 text-gray-500 dark:text-slate-400" aria-hidden="true" />
        <h3 className="font-medium text-gray-900 dark:text-slate-100">Available times for {formattedDate}</h3>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Spinner size="md" color="primary" label="Loading available times..." />
        </div>
      )}

      {!isLoading && !hasAvailableSlots && (
        <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4 text-center">
          <p className="text-amber-700 dark:text-amber-300">
            No available times for this date. Please select another date.
          </p>
        </div>
      )}

      {!isLoading && hasAvailableSlots && (
        <>
          <TimeSlotGroup
            label="Morning"
            slots={morning}
            selectedTime={selectedTime}
            onSelect={onTimeSelect}
          />

          <TimeSlotGroup
            label="Afternoon"
            slots={afternoon}
            selectedTime={selectedTime}
            onSelect={onTimeSelect}
          />

          <TimeSlotGroup
            label="Evening"
            slots={evening}
            selectedTime={selectedTime}
            onSelect={onTimeSelect}
          />

          {unavailableCount > 0 && (
            <p className="mt-4 text-sm text-gray-500 dark:text-slate-400 text-center">
              {unavailableCount} time slot
              {unavailableCount > 1 ? 's are' : ' is'} already booked
            </p>
          )}
        </>
      )}
    </div>
  );
};

TimeSlotPicker.displayName = 'TimeSlotPicker';
